/** @format */

import { useCallback, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  GA4_COMPONENT_NAME,
  GA4_COMPONENT_POSITION,
  GA4_COMPONENT_TYPE
} from 'src/analytics/componentInteractionGa4';
import { PrintIcon, ShareIcon } from 'src/components/icons';
import useGa4Analytics from 'src/hooks/useGa4Analytics';

type ExtranetContentActionButtonsProps = {
  onPrint?: (event: MouseEvent<HTMLButtonElement>) => void;
  onShare?: (event: MouseEvent<HTMLButtonElement>) => void;
  className?: string;
  componentPosition?: string;
};

export default function ExtranetContentActionButtons({
  onPrint,
  onShare,
  className = '',
  componentPosition = GA4_COMPONENT_POSITION.EXTRANET_CONTENT_DETAIL
}: ExtranetContentActionButtonsProps) {
  const { t } = useTranslation();
  const { trackComponentInteraction } = useGa4Analytics();

  const handlePrintClick = useCallback(
    event => {
      trackComponentInteraction({
        component_name: GA4_COMPONENT_NAME.CONTENT_PRINT,
        component_type: GA4_COMPONENT_TYPE.BUTTON,
        component_position: componentPosition
      });
      onPrint?.(event);
    },
    [componentPosition, onPrint, trackComponentInteraction]
  );

  const handleShareClick = useCallback(
    event => {
      trackComponentInteraction({
        component_name: GA4_COMPONENT_NAME.CONTENT_SHARE,
        component_type: GA4_COMPONENT_TYPE.BUTTON,
        component_position: componentPosition
      });
      onShare?.(event);
    },
    [componentPosition, onShare, trackComponentInteraction]
  );

  return (
    <>
      <button
        type="button"
        className={className}
        onClick={handlePrintClick}
        aria-label={t('extranet.printAria')}>
        <PrintIcon />
      </button>
      <button
        type="button"
        className={className}
        onClick={handleShareClick}
        aria-label={t('extranet.shareAria')}>
        <ShareIcon />
      </button>
    </>
  );
}
